import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save, ChevronDown, ChevronUp, Upload, Loader2 } from "lucide-react";
import {
  adminListClientProjects, adminCustomers, adminCreateClientProject, adminUpdateClientProject,
  adminDeleteClientProject, adminAddUpdate, adminDeleteUpdate, adminAddDocument, adminDeleteDocument,
  uploadFile, formatApiError,
} from "@/lib/api";

const inp = "w-full bg-transparent border border-olive/20 rounded-sm px-3 py-2 text-sm focus:border-olive focus:outline-none";
const STAGES = ["Discovery", "Booking", "Design", "Execution", "Handover"];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export default function AdminClients() {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    adminListClientProjects().then(setProjects).catch(err);
    adminCustomers().then(setCustomers).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = { customer_username: "", title: "", address: "", stage: "Discovery", status: "In Progress", completion_percent: 0, start_date: "", expected_handover: "" };
  const [draft, setDraft] = useState(blank);
  const create = async () => {
    if (!draft.customer_username || !draft.title) return toast.error("Select a customer and enter a title");
    try { await adminCreateClientProject(draft); setDraft(blank); load(); toast.success("Project created"); } catch (e) { err(e); }
  };

  return (
    <div data-testid="clients-tab" className="space-y-8">
      <div className="border border-olive/20 rounded-md p-6 bg-cream/50">
        <p className="overline text-bronze mb-4">Create Client Project</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select data-testid="cp-customer" className={inp} value={draft.customer_username} onChange={(e) => setDraft({ ...draft, customer_username: e.target.value })}>
            <option value="">Select customer…</option>
            {customers.map((c) => <option key={c.username} value={c.username}>{c.name || c.username} ({c.username})</option>)}
          </select>
          <input placeholder="Project title" data-testid="cp-title" className={inp} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <input placeholder="Address" className={inp} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
          <input placeholder="% complete" type="number" min="0" max="100" className={inp} value={draft.completion_percent} onChange={(e) => setDraft({ ...draft, completion_percent: e.target.value })} />
          <select className={inp} value={draft.stage} onChange={(e) => setDraft({ ...draft, stage: e.target.value })}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
          <input placeholder="Status" className={inp} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} />
          <input type="date" title="Start date" className={inp} value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
          <input type="date" title="Expected handover" className={inp} value={draft.expected_handover} onChange={(e) => setDraft({ ...draft, expected_handover: e.target.value })} />
        </div>
        <button onClick={create} data-testid="cp-create" className="mt-4 inline-flex items-center gap-2 bg-olive text-beige px-6 py-2.5 rounded-sm text-xs uppercase tracking-[0.2em] hover:bg-[#21463c]"><Plus size={15} /> Create</button>
        {customers.length === 0 && <p className="text-ink/50 text-sm mt-3">No customer accounts yet. Ask a client to sign up at /signup first.</p>}
      </div>

      <div className="space-y-4">
        {projects.map((p) => (
          <ProjectRow key={p.id} p={p} open={openId === p.id} toggle={() => setOpenId(openId === p.id ? null : p.id)} reload={load} />
        ))}
        {projects.length === 0 && <p className="text-ink/50">No client projects yet.</p>}
      </div>
    </div>
  );
}

function ProjectRow({ p, open, toggle, reload }) {
  const [core, setCore] = useState({ stage: p.stage, status: p.status, completion_percent: p.completion_percent, address: p.address, expected_handover: p.expected_handover || "" });
  const saveCore = async () => { try { await adminUpdateClientProject(p.id, core); toast.success("Saved"); reload(); } catch (e) { err(e); } };
  const del = async () => { try { await adminDeleteClientProject(p.id); toast.success("Deleted"); reload(); } catch (e) { err(e); } };

  const [upd, setUpd] = useState({ date: "", title: "", note: "", photos: "" });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoFileRef = useRef(null);
  const onPhotoFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      const results = await Promise.all(files.map((f) => uploadFile(f, "photo", p.id)));
      const urls = results.map((r) => r.url).join(", ");
      setUpd((u) => ({ ...u, photos: u.photos ? `${u.photos}, ${urls}` : urls }));
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (ex) { err(ex); }
    finally { setUploadingPhotos(false); if (photoFileRef.current) photoFileRef.current.value = ""; }
  };
  const addUpdate = async () => {
    if (!upd.title) return toast.error("Update title required");
    try { await adminAddUpdate(p.id, upd); setUpd({ date: "", title: "", note: "", photos: "" }); toast.success("Update added"); reload(); } catch (e) { err(e); }
  };
  const [doc, setDoc] = useState({ type: "Invoice", title: "", url: "", amount: "", status: "", date: "" });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileRef = useRef(null);
  const onDocFilePicked = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const res = await uploadFile(file, "document", p.id);
      setDoc((d) => ({ ...d, url: res.url, title: d.title || res.original_filename }));
      toast.success("Document uploaded");
    } catch (ex) { err(ex); }
    finally { setUploadingDoc(false); if (docFileRef.current) docFileRef.current.value = ""; }
  };
  const addDoc = async () => {
    if (!doc.title || !doc.url) return toast.error("Document title and file/URL required");
    try { await adminAddDocument(p.id, doc); setDoc({ type: "Invoice", title: "", url: "", amount: "", status: "", date: "" }); toast.success("Document added"); reload(); } catch (e) { err(e); }
  };

  return (
    <div className="border border-olive/15 rounded-md" data-testid={`cp-row-${p.id}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between gap-4 p-4 text-left">
        <div>
          <span className="font-display text-2xl text-olive">{p.title}</span>
          <span className="text-ink/50 text-sm ml-3">{p.customer_username} · {p.stage} · {p.completion_percent}%</span>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {open && (
        <div className="p-5 pt-0 space-y-6">
          {/* core */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <select className={inp} value={core.stage} onChange={(e) => setCore({ ...core, stage: e.target.value })}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
            <input className={inp} value={core.status} onChange={(e) => setCore({ ...core, status: e.target.value })} placeholder="Status" />
            <input type="number" min="0" max="100" data-testid={`cp-percent-${p.id}`} className={inp} value={core.completion_percent} onChange={(e) => setCore({ ...core, completion_percent: e.target.value })} placeholder="% complete" />
            <input type="date" className={inp} value={core.expected_handover} onChange={(e) => setCore({ ...core, expected_handover: e.target.value })} />
            <div className="flex gap-2">
              <button onClick={saveCore} data-testid={`cp-save-${p.id}`} className="p-2.5 bg-olive text-beige rounded-sm hover:bg-[#21463c]"><Save size={15} /></button>
              <button onClick={del} className="p-2.5 border border-red-300 text-red-600 rounded-sm hover:bg-red-50"><Trash2 size={15} /></button>
            </div>
          </div>

          {/* add update */}
          <div className="bg-cream/50 border border-olive/15 rounded-md p-4">
            <p className="overline text-bronze mb-3">Add Daily Update (upload photos or paste URLs)</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input type="date" className={inp} value={upd.date} onChange={(e) => setUpd({ ...upd, date: e.target.value })} />
              <input placeholder="Title" data-testid={`cp-upd-title-${p.id}`} className={inp} value={upd.title} onChange={(e) => setUpd({ ...upd, title: e.target.value })} />
              <input placeholder="Note" className={`${inp} lg:col-span-2`} value={upd.note} onChange={(e) => setUpd({ ...upd, note: e.target.value })} />
              <input placeholder="Photo URLs (comma separated)" className={`${inp} lg:col-span-3`} value={upd.photos} onChange={(e) => setUpd({ ...upd, photos: e.target.value })} />
              <div className="flex gap-2">
                <input ref={photoFileRef} type="file" accept="image/*" multiple hidden onChange={onPhotoFilesPicked} data-testid={`cp-upd-file-${p.id}`} />
                <button onClick={() => photoFileRef.current?.click()} disabled={uploadingPhotos} className="inline-flex items-center gap-2 border border-olive text-olive px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] hover:bg-olive hover:text-beige transition-colors">
                  {uploadingPhotos ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                </button>
                <button onClick={addUpdate} data-testid={`cp-upd-add-${p.id}`} className="inline-flex items-center justify-center gap-2 bg-olive text-beige px-4 py-2 rounded-sm text-xs uppercase tracking-[0.15em] hover:bg-[#21463c]"><Plus size={14} /> Add</button>
              </div>
            </div>
            {p.updates?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {p.updates.map((u) => (
                  <li key={u.id} className="flex justify-between text-sm border-b border-olive/10 py-1.5">
                    <span>{u.date} — {u.title} {u.photos?.length ? `(${u.photos.length} photos)` : ""}</span>
                    <button onClick={async () => { await adminDeleteUpdate(p.id, u.id); reload(); }} className="text-red-600"><Trash2 size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* add document */}
          <div className="bg-cream/50 border border-olive/15 rounded-md p-4">
            <p className="overline text-bronze mb-3">Attach Document (upload a file or paste a URL)</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <select className={inp} value={doc.type} onChange={(e) => setDoc({ ...doc, type: e.target.value })}>{["Invoice", "Contract", "Receipt", "Other"].map((t) => <option key={t}>{t}</option>)}</select>
              <input placeholder="Title" data-testid={`cp-doc-title-${p.id}`} className={inp} value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
              <input placeholder="File URL or upload →" data-testid={`cp-doc-url-${p.id}`} className={`${inp} lg:col-span-2`} value={doc.url} onChange={(e) => setDoc({ ...doc, url: e.target.value })} />
              <input placeholder="Amount (₹)" className={inp} value={doc.amount} onChange={(e) => setDoc({ ...doc, amount: e.target.value })} />
              <input placeholder="Status (e.g. Paid)" className={inp} value={doc.status} onChange={(e) => setDoc({ ...doc, status: e.target.value })} />
              <input type="date" className={inp} value={doc.date} onChange={(e) => setDoc({ ...doc, date: e.target.value })} />
              <div className="flex gap-2">
                <input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" hidden onChange={onDocFilePicked} data-testid={`cp-doc-file-${p.id}`} />
                <button onClick={() => docFileRef.current?.click()} disabled={uploadingDoc} className="inline-flex items-center gap-2 border border-olive text-olive px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] hover:bg-olive hover:text-beige transition-colors">
                  {uploadingDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                </button>
                <button onClick={addDoc} data-testid={`cp-doc-add-${p.id}`} className="inline-flex items-center justify-center gap-2 bg-olive text-beige px-4 py-2 rounded-sm text-xs uppercase tracking-[0.15em] hover:bg-[#21463c]"><Plus size={14} /> Attach</button>
              </div>
            </div>
            {p.documents?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {p.documents.map((d) => (
                  <li key={d.id} className="flex justify-between text-sm border-b border-olive/10 py-1.5">
                    <span>{d.type} — {d.title} {d.amount ? `· ₹${d.amount}` : ""} {d.status ? `· ${d.status}` : ""}</span>
                    <button onClick={async () => { await adminDeleteDocument(p.id, d.id); reload(); }} className="text-red-600"><Trash2 size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
